import { Recipe } from '../api/Recipe';
import { SupabaseService, supabase } from './SupabaseClient';
import { serviceMappers } from './serviceMappers';
import { RECIPE_WITH_PUBLIC_AUTHOR_SELECT } from './serviceSelectors';
import { buildFavoriteUpsertRows, orderByReferenceIds } from './serviceHelpers';

interface FavoriteRow {
  recipe_id: string;
  created_at: string;
}

export class FavoriteService extends SupabaseService {
  private throwIfQueryFailed(error: { message: string } | null): void {
    if (error) {
      throw new Error(error.message);
    }
  }

  private async fetchFavoriteRows(): Promise<FavoriteRow[]> {
    const supabaseClient = await this.getClient();
    const { data: favoriteRows, error } = await supabaseClient
      .from('recipe_favorites')
      .select('recipe_id, created_at')
      .order('created_at', { ascending: false });

    this.throwIfQueryFailed(error);

    return (favoriteRows || []) as FavoriteRow[];
  }

  private async fetchRecipesByIds(recipeIds: string[]): Promise<Recipe[]> {
    const { data: recipesData, error } = await supabase
      .from('recipes')
      .select(RECIPE_WITH_PUBLIC_AUTHOR_SELECT)
      .in('id', recipeIds);

    this.throwIfQueryFailed(error);

    return serviceMappers.normalizePublicRecipes(((recipesData || []) as unknown) as Recipe[]);
  }

  private async upsertFavoriteRows(
    favoriteRows: Array<{ recipe_id: string; author_id: number }>
  ): Promise<void> {
    const supabaseClient = await this.getClient();
    const { error } = await supabaseClient
      .from('recipe_favorites')
      .upsert(
        favoriteRows,
        {
          onConflict: 'recipe_id,author_id',
          ignoreDuplicates: true,
        }
      );

    this.throwIfQueryFailed(error);
  }

  public async listMyFavorites(): Promise<Recipe[]> {
    const favoriteRows = await this.fetchFavoriteRows();
    const orderedRecipeIds = favoriteRows.map((row) => row.recipe_id);
    if (orderedRecipeIds.length === 0) {
      return [];
    }

    const recipes = await this.fetchRecipesByIds(orderedRecipeIds);
    return orderByReferenceIds(recipes, orderedRecipeIds);
  }

  public async addFavorite(recipeId: string, authorId: number): Promise<void> {
    await this.upsertFavoriteRows([{ recipe_id: recipeId, author_id: authorId }]);
  }

  public async addFavorites(recipeIds: string[], authorId: number): Promise<void> {
    const favoriteRows = buildFavoriteUpsertRows(recipeIds, authorId);
    if (favoriteRows.length === 0) {
      return;
    }

    await this.upsertFavoriteRows(favoriteRows);
  }

  public async removeFavorite(recipeId: string, authorId: number): Promise<void> {
    const supabaseClient = await this.getClient();
    const { error } = await supabaseClient
      .from('recipe_favorites')
      .delete()
      .eq('recipe_id', recipeId)
      .eq('author_id', authorId);

    this.throwIfQueryFailed(error);
  }
}

export const favoriteService = new FavoriteService();
